/**
 * Hero phone — scroll-driven rotateY (home page).
 *
 * The mockup starts at rotateY(-25deg) and swings to +25deg as the
 * user scrolls the first ~60% of the page, with a light parallax lift
 * up to 26px. Motion is spring-smoothed (current += Δ·0.08 per rAF
 * frame) so it never snaps. Perspective (1100px) lives on the parent
 * container; this module only writes translateY/rotateY on the img.
 *
 * prefers-reduced-motion: the pose still tracks the scroll position
 * (1:1 mapping of the user's own gesture — no autonomous motion), but
 * the spring smoothing is dropped so nothing moves by itself. Small
 * screens (≤700px) keep the CSS `transform: none` override — we skip
 * inline writes there so the phone can't clip the viewport.
 */

const EASE = 0.08;
const YAW_FROM = -25;
const YAW_RANGE = 50;
const LIFT_MAX = -26;
const SCROLL_SPAN = 0.6;

declare global {
  interface Window {
    __otHeroPhoneInit?: boolean;
  }
}

function wire(): void {
  const el = document.querySelector<HTMLElement>('[data-hero-phone]:not([data-phone-wired])');
  if (!el) return;
  el.dataset['phoneWired'] = '1';

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const small = window.matchMedia('(max-width: 700px)');

  let current = 0;
  let target = 0;
  let rafId: number | null = null;

  const progress = (): number => {
    const span = (document.documentElement.scrollHeight - window.innerHeight) * SCROLL_SPAN;
    if (span <= 0) return 0;
    return Math.min(1, Math.max(0, window.scrollY / span));
  };

  const apply = (p: number): void => {
    if (small.matches) {
      // Let the CSS transform:none override rule win on phones.
      el.style.transform = '';
      return;
    }
    const yaw = YAW_FROM + p * YAW_RANGE;
    const lift = p * LIFT_MAX;
    el.style.transform = `translateY(${lift}px) rotateY(${yaw}deg)`;
  };

  const frame = (): void => {
    // Element got swapped out by a view transition — stop the loop.
    if (!el.isConnected) {
      rafId = null;
      return;
    }
    current += (target - current) * EASE;
    if (Math.abs(target - current) < 0.0005) {
      current = target;
      apply(current);
      rafId = null; // settled — restart on the next scroll delta
      return;
    }
    apply(current);
    rafId = requestAnimationFrame(frame);
  };

  const kick = (): void => {
    target = progress();
    if (reduced.matches) {
      // Reduce Motion: no spring — snap 1:1 to the scroll position so
      // the phone only ever moves exactly as far as the user scrolled.
      current = target;
      apply(current);
      return;
    }
    if (rafId === null) rafId = requestAnimationFrame(frame);
  };

  window.addEventListener('scroll', kick, { passive: true });
  window.addEventListener('resize', kick, { passive: true });

  // First paint: jump straight to the correct pose (no intro tween).
  current = target = progress();
  apply(current);
}

export function initHeroPhone(): void {
  if (typeof window === 'undefined' || window.__otHeroPhoneInit) return;
  window.__otHeroPhoneInit = true;
  wire();
  document.addEventListener('astro:page-load', wire);
}
